// src/pages/AdminErrorReports.tsx
//
// Triage view for errors reported by SI client apps. Lists everything
// that came in through POST /api/errors/report, filterable by app,
// severity, status, and a free-text search across message/email/source.
// Click a row to open the detail card with stack trace, context, and
// status/notes editor.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchErrorApps,
  fetchErrorReport,
  fetchErrorReports,
  updateErrorReport,
  SEVERITY_COLORS,
  SEVERITY_OPTIONS,
  STATUS_COLORS,
  STATUS_OPTIONS,
  type ErrorAppSummary,
  type ErrorReportDetail,
  type ErrorReportSummary,
  type ErrorSeverity,
  type ErrorStatus,
} from '../utils/errorReportsApi';

const PAGE_SIZE = 100;

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const Badge: React.FC<{ bg: string; fg: string; children: React.ReactNode }> = ({ bg, fg, children }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '0.2rem 0.6rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: bg,
      color: fg,
      textTransform: 'capitalize',
    }}
  >
    {children}
  </span>
);

const FilterSelect: React.FC<{
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  minWidth?: number;
}> = ({ label, value, options, onChange, minWidth = 140 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
    <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280' }}>{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: '0.5rem 0.75rem',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        backgroundColor: 'white',
        minWidth,
      }}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const JsonBlock: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined) return <em style={{ color: '#9ca3af' }}>(none)</em>;
  let formatted: string;
  try {
    formatted = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  } catch {
    formatted = String(value);
  }
  return (
    <pre
      style={{
        margin: 0,
        padding: '0.75rem',
        backgroundColor: '#0f172a',
        color: '#e2e8f0',
        borderRadius: '6px',
        fontSize: '0.8rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: '24rem',
        overflow: 'auto',
      }}
    >
      {formatted}
    </pre>
  );
};

const AdminErrorReports: React.FC = () => {
  const { token } = useAuth();

  const [apps, setApps] = useState<ErrorAppSummary[]>([]);
  const [reports, setReports] = useState<ErrorReportSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [appFilter, setAppFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<ErrorSeverity | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ErrorStatus | 'all'>('open');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ErrorReportDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingDetail, setSavingDetail] = useState(false);

  const loadList = useCallback(async (isInitial = false) => {
    if (!token) return;
    if (isInitial) setLoading(true);
    else setRefreshing(true);
    setListError(null);
    try {
      const [appsResult, reportsResult] = await Promise.all([
        fetchErrorApps(token),
        fetchErrorReports(token, {
          appId: appFilter,
          severity: severityFilter,
          status: statusFilter,
          search: search || undefined,
          limit: PAGE_SIZE,
        }),
      ]);
      setApps(appsResult);
      setReports(reportsResult.reports);
      setTotal(reportsResult.total);
    } catch (err: any) {
      setListError(err?.message || 'Failed to load error reports');
    } finally {
      if (isInitial) setLoading(false);
      else setRefreshing(false);
    }
  }, [token, appFilter, severityFilter, statusFilter, search]);

  useEffect(() => { loadList(true); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (token) loadList(false); }, [appFilter, severityFilter, statusFilter, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detail loader — when a row is selected, fetch its full record.
  useEffect(() => {
    if (!token || selectedId == null) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    fetchErrorReport(token, selectedId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setNotesDraft(d.notes || '');
      })
      .catch((err) => {
        if (cancelled) return;
        setDetailError(err?.message || 'Failed to load error');
      })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [token, selectedId]);

  const handleStatusChange = async (newStatus: ErrorStatus) => {
    if (!token || !detail) return;
    setSavingDetail(true);
    try {
      const updated = await updateErrorReport(token, detail.id, { status: newStatus });
      setDetail(updated);
      setReports((rows) => rows.map((r) => (r.id === updated.id ? { ...r, status: updated.status } : r)));
    } catch (err: any) {
      setDetailError(err?.message || 'Failed to update status');
    } finally {
      setSavingDetail(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!token || !detail) return;
    setSavingDetail(true);
    try {
      const updated = await updateErrorReport(token, detail.id, { notes: notesDraft });
      setDetail(updated);
    } catch (err: any) {
      setDetailError(err?.message || 'Failed to save notes');
    } finally {
      setSavingDetail(false);
    }
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const appOptions = useMemo(
    () => [
      { value: 'all', label: `All apps (${apps.reduce((a, x) => a + x.total_count, 0)})` },
      ...apps.map((a) => ({ value: a.app_id, label: `${a.app_id} (${a.open_count} open / ${a.total_count})` })),
    ],
    [apps],
  );

  if (loading) {
    return (
      <div className="section container" style={{ textAlign: 'center', padding: '4rem' }}>
        <p>Loading error reports...</p>
      </div>
    );
  }

  return (
    <div className="section container">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '0.25rem' }}>Error Reports</h1>
        <p className="text-secondary" style={{ marginBottom: '1.5rem' }}>
          Errors reported by SI client apps. {total} matching report{total === 1 ? '' : 's'}.
        </p>

        {/* Filters */}
        <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <FilterSelect
              label="App"
              value={appFilter}
              options={appOptions}
              onChange={setAppFilter}
              minWidth={220}
            />
            <FilterSelect
              label="Severity"
              value={severityFilter}
              options={[{ value: 'all', label: 'All severities' }, ...SEVERITY_OPTIONS.map((s) => ({ value: s, label: s }))]}
              onChange={(v) => setSeverityFilter(v as ErrorSeverity | 'all')}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              options={[{ value: 'all', label: 'All statuses' }, ...STATUS_OPTIONS.map((s) => ({ value: s, label: s }))]}
              onChange={(v) => setStatusFilter(v as ErrorStatus | 'all')}
            />
            <form onSubmit={submitSearch} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '1 1 240px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280' }}>
                Search (message, email, source)
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="e.g. timeout, jane@…, /transactions/new"
                  style={{ flex: 1, padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
                <button type="submit" className="btn btn-secondary">Search</button>
                {search && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => { setSearchInput(''); setSearch(''); }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </form>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => loadList(false)}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {listError && (
          <div className="card" style={{ padding: '1rem', marginBottom: '1rem', backgroundColor: '#fef2f2', color: '#dc2626' }}>
            {listError}
          </div>
        )}

        {/* List */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {reports.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
              No errors match these filters.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280' }}>When</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280' }}>App</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280' }}>Severity</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280' }}>Message</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280' }}>User</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => {
                  const sev = SEVERITY_COLORS[r.severity];
                  const stat = STATUS_COLORS[r.status];
                  const isSelected = r.id === selectedId;
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedId(isSelected ? null : r.id)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#eef2ff' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem', whiteSpace: 'nowrap', color: '#374151' }}>
                        {formatDate(r.created_at)}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem' }}>
                        <div style={{ fontWeight: 500 }}>{r.app_id}</div>
                        {(r.app_version || r.platform) && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {[r.platform, r.app_version].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
                        <Badge bg={sev.bg} fg={sev.fg}>{r.severity}</Badge>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem', maxWidth: '480px' }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.message}
                        </div>
                        {r.source && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.source}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem', color: '#374151' }}>
                        {r.user_email || (r.user_id ? `#${r.user_id}` : <span style={{ color: '#9ca3af' }}>anon</span>)}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
                        <Badge bg={stat.bg} fg={stat.fg}>{r.status}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {total > reports.length && (
          <p className="text-secondary" style={{ fontSize: '0.85rem', marginTop: '0.75rem' }}>
            Showing the {reports.length} most recent of {total}. Refine filters to narrow further.
          </p>
        )}

        {/* Detail card */}
        {selectedId != null && (
          <div className="card" style={{ marginTop: '1.5rem', padding: '1.5rem' }}>
            {detailLoading && <p>Loading details…</p>}
            {detailError && (
              <div style={{ color: '#dc2626', marginBottom: '0.5rem' }}>{detailError}</div>
            )}
            {detail && !detailLoading && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Badge bg={SEVERITY_COLORS[detail.severity].bg} fg={SEVERITY_COLORS[detail.severity].fg}>
                        {detail.severity}
                      </Badge>
                      <Badge bg={STATUS_COLORS[detail.status].bg} fg={STATUS_COLORS[detail.status].fg}>
                        {detail.status}
                      </Badge>
                      <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        Error #{detail.id} · {detail.app_id}
                        {detail.app_version ? ` · v${detail.app_version}` : ''}
                        {detail.platform ? ` · ${detail.platform}` : ''}
                      </span>
                    </div>
                    <h2 style={{ marginTop: '0.5rem', marginBottom: '0.25rem', wordBreak: 'break-word' }}>
                      {detail.message}
                    </h2>
                    <p className="text-secondary" style={{ fontSize: '0.85rem', margin: 0 }}>
                      {formatDate(detail.created_at)}
                      {detail.source ? ` · ${detail.source}` : ''}
                      {detail.user_email ? ` · ${detail.user_email}` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setSelectedId(null)}
                  >
                    Close
                  </button>
                </div>

                {/* Status + notes editor */}
                <div style={{ marginTop: '1.25rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280' }}>Status</label>
                    <select
                      value={detail.status}
                      onChange={(e) => handleStatusChange(e.target.value as ErrorStatus)}
                      disabled={savingDetail}
                      style={{ padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white' }}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '1 1 320px' }}>
                    <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280' }}>Admin notes</label>
                    <textarea
                      value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value)}
                      rows={3}
                      placeholder="What did you find? What still needs to happen?"
                      style={{ padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', resize: 'vertical' }}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSaveNotes}
                      disabled={savingDetail || (notesDraft || '') === (detail.notes || '')}
                      style={{ alignSelf: 'flex-end' }}
                    >
                      {savingDetail ? 'Saving…' : 'Save notes'}
                    </button>
                  </div>
                </div>

                {/* HTTP info if present */}
                {(detail.http_url || detail.http_status || detail.http_response) && (
                  <section style={{ marginTop: '1.5rem' }}>
                    <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>HTTP</h3>
                    <div style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.5rem' }}>
                      <strong>{detail.http_method || 'GET'}</strong> {detail.http_url || '—'}
                      {detail.http_status != null && <> · status <strong>{detail.http_status}</strong></>}
                    </div>
                    {detail.http_response && <JsonBlock value={detail.http_response} />}
                  </section>
                )}

                <section style={{ marginTop: '1.5rem' }}>
                  <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Stack trace</h3>
                  <JsonBlock value={detail.stack_trace} />
                </section>

                <section style={{ marginTop: '1.5rem' }}>
                  <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Context</h3>
                  <JsonBlock value={detail.context} />
                </section>

                <section style={{ marginTop: '1.5rem' }}>
                  <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Device info</h3>
                  <JsonBlock value={detail.device_info} />
                </section>

                <section style={{ marginTop: '1.5rem', fontSize: '0.8rem', color: '#6b7280' }}>
                  <div>Fingerprint: <code>{detail.fingerprint || '—'}</code></div>
                  <div>IP: {detail.ip_address || '—'}</div>
                  <div style={{ wordBreak: 'break-word' }}>User-Agent: {detail.user_agent || '—'}</div>
                  {detail.resolved_at && (
                    <div>Resolved: {formatDate(detail.resolved_at)} (by user #{detail.resolved_by ?? '—'})</div>
                  )}
                </section>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminErrorReports;
